import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Loader2, CheckCircle2, AlertCircle, Trash2, User, AlertTriangle, Sparkles, CheckSquare, ChevronDown, UserPlus, Flame } from "lucide-react";

const MOCK_TEAM = [
  { id: 'mock-1', name: 'Алексей', role: 'Аналитик' },
  { id: 'mock-2', name: 'Мария', role: 'Дизайн' },
  { id: 'mock-3', name: 'Игорь', role: 'Разработка' },
];

function AssigneeDropdown({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedUser = MOCK_TEAM.find(u => u.id === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm font-light text-neutral-300 hover:bg-neutral-800/80 transition-colors focus:outline-none focus:border-neutral-600"
      >
        <div className="flex items-center gap-2 truncate">
          {selectedUser ? (
            <div className="flex items-center gap-2">
               <div className="w-5 h-5 rounded-full bg-neutral-800 flex items-center justify-center text-[10px] font-medium text-neutral-400">
                 {selectedUser.name.charAt(0)}
               </div>
               <span>{selectedUser.name} <span className="text-neutral-500">({selectedUser.role})</span></span>
            </div>
          ) : (
             <span className="text-neutral-500">Не назначен (Искать эксперта)</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute z-10 top-full left-0 right-0 mt-1 bg-neutral-900 border border-neutral-800 rounded-md shadow-xl overflow-hidden"
          >
            <div className="max-h-[250px] overflow-y-auto">
               <div className="p-1">
                 <div className="px-2 py-1.5 text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                   Команда проекта
                 </div>
                 {MOCK_TEAM.map(user => (
                   <button
                     key={user.id}
                     type="button"
                     onClick={() => { onChange(user.id); setIsOpen(false); }}
                     className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-neutral-800 transition-colors ${value === user.id ? 'bg-neutral-800 text-white' : 'text-neutral-300'}`}
                   >
                     <div className="w-5 h-5 rounded-full bg-neutral-800 flex items-center justify-center text-[10px] font-medium text-neutral-400">
                       {user.name.charAt(0)}
                     </div>
                     <span>{user.name} <span className="text-neutral-500">({user.role})</span></span>
                     {value === user.id && <CheckCircle2 className="w-3.5 h-3.5 ml-auto text-neutral-400" />}
                   </button>
                 ))}
               </div>

               <div className="h-px bg-neutral-800 w-full" />
               
               <div className="p-1">
                 <button
                   type="button"
                   onClick={() => { onChange(""); setIsOpen(false); }}
                   className="w-full flex items-center gap-2 px-2 py-2 text-sm text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors rounded-sm"
                 >
                   <UserPlus className="w-4 h-4 text-neutral-400" />
                   <span>Пригласить сотрудника</span>
                 </button>
               </div>

               <div className="h-px bg-neutral-800 w-full" />
               
               <div className="p-1 bg-orange-950/20">
                 <button
                   type="button"
                   onClick={() => { onChange(""); setIsOpen(false); }}
                   className="w-full flex items-center gap-2 px-2 py-2 text-sm text-orange-400 hover:text-orange-300 hover:bg-orange-900/40 transition-colors rounded-sm group"
                 >
                   <Flame className="w-4 h-4 text-orange-500 group-hover:animate-pulse" />
                   <span>Привлечь эксперта <span className="opacity-60 text-xs ml-1">(от 5000 ₽)</span></span>
                 </button>
               </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function TaskDrawer({
  task,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  isDraft = false,
  onApprove
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expectedResult, setExpectedResult] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [status, setStatus] = useState("TODO");
  const [assigneeId, setAssigneeId] = useState("");

  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const saveTimeoutRef = useRef(null);
  const hideStatusTimeoutRef = useRef(null);

  const prevTaskIdRef = useRef(null);

  // Sync internal state when task/draft ID changes or drawer opens
  useEffect(() => {
    if (task && task.id && (task.id !== prevTaskIdRef.current || !isOpen)) {
      prevTaskIdRef.current = task.id;
      if (isDraft) {
        setTitle(task.payloadJson?.title || task.title || "");
        setDescription(task.payloadJson?.description || task.description || "");
        setExpectedResult(task.payloadJson?.expectedResult || "");
        setPriority(task.payloadJson?.priority || task.priority || "MEDIUM");
        setAssigneeId(task.payloadJson?.assigneeId || task.assigneeId || "");
        setStatus("TODO");
      } else {
        setTitle(task.title || "");
        setDescription(task.description || "");
        setExpectedResult(task.expectedResult || "");
        setPriority(task.priority || "MEDIUM");
        setStatus(task.status || "TODO");
        setAssigneeId(task.assigneeId || "");
      }
    }
    if (!isOpen) {
      prevTaskIdRef.current = null;
    }
  }, [task?.id, isDraft, isOpen]);

  const triggerSave = useCallback(async (id, updates, isDraftMode) => {
    if (!id) return;
    setSaveStatus('saving');
    try {
      const token = localStorage.getItem("obstuchim_token");
      let res;
      if (isDraftMode) {
        res = await fetch("/api/drafts", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            draftId: id,
            payloadJson: updates
          })
        });
      } else {
        res = await fetch(`/api/tasks/${id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        });
      }

      if (!res.ok) throw new Error("Save failed");
      setSaveStatus('saved');
      
      if (hideStatusTimeoutRef.current) clearTimeout(hideStatusTimeoutRef.current);
      hideStatusTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2500);
    } catch (err) {
      console.error("AutoSave error:", err);
      setSaveStatus('error');
    }
  }, []);

  const debouncedSave = useCallback((id, updates, isDraftMode) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (hideStatusTimeoutRef.current) clearTimeout(hideStatusTimeoutRef.current);
    
    setSaveStatus('saving');
    saveTimeoutRef.current = setTimeout(() => {
      triggerSave(id, updates, isDraftMode);
    }, 1000);
  }, [triggerSave]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (hideStatusTimeoutRef.current) clearTimeout(hideStatusTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !task) return;

    const origTitle = isDraft ? (task.payloadJson?.title || task.title || "") : (task.title || "");
    const origDesc = isDraft ? (task.payloadJson?.description || task.description || "") : (task.description || "");
    const origExp = isDraft ? (task.payloadJson?.expectedResult || task.expectedResult || "") : (task.expectedResult || "");
    const origPriority = isDraft ? (task.payloadJson?.priority || task.priority || "MEDIUM") : (task.priority || "MEDIUM");
    const origStatus = task.status || "TODO";
    const origAssignee = isDraft ? (task.payloadJson?.assigneeId || task.assigneeId || "") : (task.assigneeId || "");

    const hasChanged = 
      title !== origTitle ||
      description !== origDesc ||
      expectedResult !== origExp ||
      priority !== origPriority ||
      (!isDraft && status !== origStatus) ||
      assigneeId !== origAssignee;

    if (hasChanged) {
      const updates = isDraft
        ? { title, description, expectedResult, priority, assigneeId }
        : { title, description, expectedResult, priority, status, assigneeId };
      
      debouncedSave(task.id, updates, isDraft);
      if (onUpdate) {
        if (isDraft) {
          onUpdate(task.id, {
            ...task,
            payloadJson: updates,
            title,
            description,
            expectedResult,
            priority,
            assigneeId
          });
        } else {
          onUpdate(task.id, updates);
        }
      }
    }
  }, [title, description, expectedResult, priority, status, assigneeId, isDraft, isOpen, task?.id, debouncedSave, onUpdate]);

  if (!isOpen || !task) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-end">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Drawer Panel */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="relative w-full max-w-4xl h-full flex z-10 shadow-2xl"
        >
          {/* Main Form Panel */}
          <div className="flex-1 bg-[#141414] border-l border-neutral-800 flex flex-col h-full border-r border-neutral-800/50">
            {/* Header */}
            <div className="flex flex-col gap-2 p-6 border-b border-neutral-800/60 bg-[#1c1c1c]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isDraft ? (
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-accent-400 bg-accent-500/10 px-2.5 py-0.5 rounded border border-accent-500/20">
                    <Sparkles className="w-3 h-3" />
                    Черновик от ИИ (Редактирование)
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">
                    Редактирование задачи
                  </span>
                )}

                {/* Auto-save indicator */}
                <div className="h-4 flex items-center">
                  <AnimatePresence mode="wait">
                    {saveStatus === 'saving' && (
                      <motion.div
                        key="saving"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex items-center gap-1.5 text-neutral-500 text-[10px] font-extralight"
                      >
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Сохранение...</span>
                      </motion.div>
                    )}
                    {saveStatus === 'saved' && (
                      <motion.div
                        key="saved"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex items-center gap-1.5 text-green-500/70 text-[10px] font-extralight"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        <span>Сохранено</span>
                      </motion.div>
                    )}
                    {saveStatus === 'error' && (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex items-center gap-1.5 text-red-400 text-[10px] font-extralight"
                      >
                        <AlertCircle className="w-3 h-3" />
                        <span>Ошибка</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                title="Закрыть панель"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Название задачи"
              rows={2}
              className="w-full bg-transparent border-none text-xl font-normal text-neutral-100 focus:outline-none focus:ring-0 p-0 placeholder:text-neutral-600 resize-none"
            />
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Status & Priority */}
            {isDraft ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-neutral-400" />
                    Приоритет
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm font-light text-neutral-300 focus:outline-none focus:border-accent-500 transition-colors appearance-none cursor-pointer hover:bg-neutral-800/80"
                  >
                    <option value="LOW">Низкий</option>
                    <option value="MEDIUM">Средний</option>
                    <option value="HIGH">Высокий</option>
                    <option value="URGENT">Срочно</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-neutral-400" />
                    Исполнитель
                  </label>
                  <AssigneeDropdown value={assigneeId} onChange={setAssigneeId} />
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
                      Статус
                    </label>
                    <select
                      value={status === 'REVIEW' ? 'IN_REVIEW' : status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm font-light text-neutral-300 focus:outline-none focus:border-primary-700 transition-colors appearance-none cursor-pointer hover:bg-neutral-800/80"
                    >
                      <option value="TODO">В планах</option>
                      <option value="IN_PROGRESS">В работе</option>
                      <option value="IN_REVIEW">На проверке</option>
                      <option value="DONE">Завершена</option>
                      <option value="CANCELLED">Отменена</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
                      Приоритет
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm font-light text-neutral-300 focus:outline-none focus:border-primary-700 transition-colors appearance-none cursor-pointer hover:bg-neutral-800/80"
                    >
                      <option value="LOW">Низкий</option>
                      <option value="MEDIUM">Средний</option>
                      <option value="HIGH">Высокий</option>
                      <option value="URGENT">Срочно</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
                    Исполнитель
                  </label>
                  <AssigneeDropdown value={assigneeId} onChange={setAssigneeId} />
                </div>
              </>
            )}

            {/* Description */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
                Описание задачи
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Подробное описание задачи..."
                rows={4}
                className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-3 text-sm font-light text-neutral-300 focus:outline-none focus:border-primary-700 transition-colors resize-y min-h-[100px]"
              />
            </div>

            {/* Expected Result */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
                Ожидаемый результат (DoD)
              </label>
              <textarea
                value={expectedResult}
                onChange={(e) => setExpectedResult(e.target.value)}
                placeholder="Критерии готовности и измеримый результат..."
                rows={3}
                className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-3 text-sm font-light text-neutral-300 focus:outline-none focus:border-primary-700 transition-colors resize-y"
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-neutral-800 bg-[#1c1c1c] flex justify-between items-center mt-auto gap-3">
            {isDraft ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onDelete(task.id);
                    onClose();
                  }}
                  className="flex items-center gap-2 text-neutral-400 hover:text-red-400 hover:bg-red-500/10 px-3 py-2 rounded-md transition-colors text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Отклонить
                </button>
                
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-neutral-400 hover:text-white transition-colors text-sm font-light"
                  >
                    Закрыть
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (onApprove) {
                        await onApprove(task.id);
                      }
                      onClose();
                    }}
                    className="flex items-center gap-2 bg-accent-600 hover:bg-accent-500 text-white px-5 py-2 rounded-md transition-colors text-sm font-medium shadow-lg shadow-accent-900/20"
                  >
                    <CheckSquare className="w-4 h-4" />
                    Утвердить в работу
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onDelete(task.id);
                    onClose();
                  }}
                  className="flex items-center gap-2 text-red-500 hover:text-red-400 hover:bg-red-500/10 px-3 py-2 rounded-md transition-colors text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Удалить задачу
                </button>
                
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-neutral-400 hover:text-white transition-colors text-sm font-light"
                  >
                    Закрыть
                  </button>
                  
                  {(status === 'TODO' || status === 'IN_PROGRESS') && (
                    <button
                      type="button"
                      onClick={() => setStatus('DONE')}
                      className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-md transition-colors text-sm font-medium shadow-lg shadow-emerald-900/20"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Завершить задачу
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          </div>
          
          {/* Right Panel: Timeline & Legacy */}
          <div className="w-[380px] bg-[#0a0a0a] flex flex-col h-full border-l border-neutral-800 hidden md:flex">
            <div className="p-6 border-b border-neutral-800/60 bg-[#141414] shrink-0">
              <h4 className="text-sm font-medium text-neutral-200">История и обсуждение</h4>
              <p className="text-xs text-neutral-500 mt-1">Журнал решений (Legacy Log)</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Activity 1 */}
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-neutral-800 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] text-neutral-400">AI</span>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 mb-1">
                    <span className="text-neutral-300 font-medium">DeepSeek AI</span> сгенерировал задачу
                    <span className="ml-2">12 окт, 14:00</span>
                  </div>
                  <div className="text-sm text-neutral-400 bg-[#141414] p-3 rounded-lg border border-neutral-800">
                    Задача декомпозирована на основе загруженного документа "План Q4.pdf".
                  </div>
                </div>
              </div>

              {/* Activity 2 */}
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-indigo-900/30 border border-indigo-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] text-indigo-400">В</span>
                </div>
                <div>
                  <div className="text-xs text-neutral-500 mb-1">
                    <span className="text-neutral-300 font-medium">Вы</span> изменили описание
                    <span className="ml-2">14 окт, 09:30</span>
                  </div>
                  <div className="text-sm text-neutral-400 bg-[#141414] p-3 rounded-lg border border-neutral-800">
                    Уточнил критерии приемки, добавил требование про безопасность (152-ФЗ).
                  </div>
                </div>
              </div>

              {/* Activity 3 (AI Suggestion) */}
              {!isDraft && (status === 'TODO' || status === 'IN_PROGRESS') && (
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-900/30 border border-orange-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Flame className="w-3.5 h-3.5 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">
                      <span className="text-orange-400 font-medium">AI-Ассистент</span>
                      <span className="ml-2">Только что</span>
                    </div>
                    <div className="text-sm text-neutral-300 bg-orange-950/20 p-3 rounded-lg border border-orange-900/30">
                      Задача находится в статусе «{status === 'TODO' ? 'В планах' : 'В работе'}» дольше ожидаемого. <br/><br/>
                      Рекомендую привлечь эксперта для разблокировки. В нашей базе есть 3 подходящих специалиста.
                      
                      <button className="mt-3 w-full bg-orange-600/10 hover:bg-orange-600/20 text-orange-500 border border-orange-500/20 py-1.5 px-3 rounded text-xs font-medium transition-colors">
                        Подобрать эксперта
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Comment Input */}
            <div className="p-4 bg-[#141414] border-t border-neutral-800 mt-auto">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Оставить комментарий (сохранится в легаси)..." 
                  className="w-full bg-neutral-900 border border-neutral-800 rounded-full pl-4 pr-10 py-2.5 text-sm text-neutral-200 focus:outline-none focus:border-neutral-600 transition-colors"
                />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full hover:bg-neutral-800 transition-colors text-neutral-500 hover:text-white">
                  →
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
